import { Place, PlaceClassification } from '~/models/Place'
import { Location } from '~/models/Spot'

export default class PlaceEntityMapper {
	static toPlace(place: google.maps.places.Place): Place {
		const images = this.toImage(place.photos)
		return {
			id: place.id,
			name: place.displayName || undefined,
			schedule: place.regularOpeningHours?.weekdayDescriptions,
			website: place.websiteURI || undefined,
			mapUrl: place.googleMapsURI || undefined,
			phone: place.nationalPhoneNumber || undefined,
			rating: place.rating || undefined,
			type: this.toType(place.types?.[0]),
			images: [images],
			cover: images,
			address: place.formattedAddress || undefined,
			location: this.toLocation(place.location || undefined),
		}
	}

	static toPlaces(places: google.maps.places.Place[]): Place[] {
		return places.map((place) => this.toPlace(place))
	}

	private static toType(type?: string): PlaceClassification {
		const defaultType: PlaceClassification = 'touristAttraction'
		if (!type) return defaultType
		return placeTypes[type] || defaultType
	}

	static toLocation(location?: google.maps.LatLng): Location | undefined {
		if (!location) return
		return { latitude: location.lat(), longitude: location.lng() }
	}

	private static toImage(photos?: google.maps.places.Photo[]) {
		if (!photos || photos.length === 0) return ''
		return photos[0].getURI({ maxHeight: 150 })
	}
}

const placeTypes: Record<string, PlaceClassification> = {
	airport: 'airport',
	airstrip: 'airport',
	international_airport: 'airport',
	heliport: 'airport',
	bed_and_breakfast: 'accommodation',
	budget_japanese_inn: 'accommodation',
	campground: 'accommodation',
	camping_cabin: 'accommodation',
	condominium_complex: 'accommodation',
	cottage: 'accommodation',
	extended_stay_hotel: 'accommodation',
	farmstay: 'accommodation',
	guest_house: 'accommodation',
	hostel: 'accommodation',
	hotel: 'accommodation',
	housing_complex: 'accommodation',
	inn: 'accommodation',
	japanese_inn: 'accommodation',
	lodging: 'accommodation',
	mobile_home_park: 'accommodation',
	motel: 'accommodation',
	private_guest_room: 'accommodation',
	resort_hotel: 'accommodation',
	rv_park: 'accommodation',
	bar: 'drink',
	bar_and_grill: 'drink',
	coffee_shop: 'drink',
	juice_shop: 'drink',
	liquor_store: 'drink',
	night_club: 'drink',
	pub: 'drink',
	tea_house: 'drink',
	wine_bar: 'drink',
	acai_shop: 'food',
	afghani_restaurant: 'food',
	african_restaurant: 'food',
	american_restaurant: 'food',
	asian_restaurant: 'food',
	bagel_shop: 'food',
	bakery: 'food',
	barbecue_restaurant: 'food',
	brazilian_restaurant: 'food',
	breakfast_restaurant: 'food',
	brunch_restaurant: 'food',
	buffet_restaurant: 'food',
	cafe: 'food',
	cafeteria: 'food',
	candy_store: 'food',
	cat_cafe: 'food',
	chinese_restaurant: 'food',
	chocolate_factory: 'food',
	chocolate_shop: 'food',
	confectionery: 'food',
	deli: 'food',
	dessert_restaurant: 'food',
	dessert_shop: 'food',
	diner: 'food',
	dog_cafe: 'food',
	donut_shop: 'food',
	fast_food_restaurant: 'food',
	fine_dining_restaurant: 'food',
	food_court: 'food',
	french_restaurant: 'food',
	greek_restaurant: 'food',
	hamburger_restaurant: 'food',
	ice_cream_shop: 'food',
	indian_restaurant: 'food',
	indonesian_restaurant: 'food',
	italian_restaurant: 'food',
	japanese_restaurant: 'food',
	korean_restaurant: 'food',
	lebanese_restaurant: 'food',
	meal_delivery: 'food',
	meal_takeaway: 'food',
	mediterranean_restaurant: 'food',
	mexican_restaurant: 'food',
	middle_eastern_restaurant: 'food',
	pizza_restaurant: 'food',
	ramen_restaurant: 'food',
	restaurant: 'food',
	sandwich_shop: 'food',
	seafood_restaurant: 'food',
	spanish_restaurant: 'food',
	steak_house: 'food',
	sushi_restaurant: 'food',
	thai_restaurant: 'food',
	turkish_restaurant: 'food',
	vegan_restaurant: 'food',
	vegetarian_restaurant: 'food',
	vietnamese_restaurant: 'food',
	asian_grocery_store: 'shopping',
	auto_parts_store: 'shopping',
	bicycle_store: 'shopping',
	book_store: 'shopping',
	butcher_shop: 'shopping',
	cell_phone_store: 'shopping',
	clothing_store: 'shopping',
	convenience_store: 'shopping',
	department_store: 'shopping',
	discount_store: 'shopping',
	electronics_store: 'shopping',
	florist: 'shopping',
	food_store: 'shopping',
	furniture_store: 'shopping',
	gift_shop: 'shopping',
	grocery_store: 'shopping',
	hardware_store: 'shopping',
	home_goods_store: 'shopping',
	home_improvement_store: 'shopping',
	jewelry_store: 'shopping',
	market: 'shopping',
	pet_store: 'shopping',
	shoe_store: 'shopping',
	shopping_mall: 'shopping',
	sporting_goods_store: 'shopping',
	store: 'shopping',
	supermarket: 'shopping',
	warehouse_store: 'shopping',
	wholesaler: 'shopping',
	art_gallery: 'culture',
	art_studio: 'culture',
	auditorium: 'culture',
	comedy_club: 'culture',
	concert_hall: 'culture',
	cultural_center: 'culture',
	cultural_landmark: 'culture',
	dance_hall: 'culture',
	historical_place: 'culture',
	library: 'culture',
	movie_rental: 'culture',
	movie_theater: 'culture',
	museum: 'culture',
	opera_house: 'culture',
	performing_arts_theater: 'culture',
	philharmonic_hall: 'culture',
	planetarium: 'culture',
	botanical_garden: 'outdoorLocation',
	dog_park: 'outdoorLocation',
	farm: 'outdoorLocation',
	garden: 'outdoorLocation',
	hiking_area: 'outdoorLocation',
	marina: 'outdoorLocation',
	national_park: 'outdoorLocation',
	park: 'outdoorLocation',
	picnic_ground: 'outdoorLocation',
	ranch: 'outdoorLocation',
	rest_stop: 'outdoorLocation',
	skateboard_park: 'outdoorLocation',
	state_park: 'outdoorLocation',
	wildlife_park: 'outdoorLocation',
	wildlife_refuge: 'outdoorLocation',
	zoo: 'outdoorLocation',
	accounting: 'service',
	adventure_sports_center: 'service',
	amusement_center: 'service',
	astrologer: 'service',
	atm: 'service',
	bank: 'service',
	barber_shop: 'service',
	beautician: 'service',
	beauty_salon: 'service',
	body_art_service: 'service',
	car_dealer: 'service',
	car_repair: 'service',
	car_wash: 'service',
	catering_service: 'service',
	child_care_agency: 'service',
	chiropractor: 'service',
	consultant: 'service',
	corporate_office: 'service',
	courier_service: 'service',
	dental_clinic: 'service',
	dentist: 'service',
	doctor: 'service',
	drugstore: 'service',
	electrician: 'service',
	fire_station: 'service',
	fishing_charter: 'service',
	fishing_pond: 'service',
	fitness_center: 'service',
	food_delivery: 'service',
	foot_care: 'service',
	funeral_home: 'service',
	general_contractor: 'service',
	golf_course: 'service',
	government_office: 'service',
	gym: 'service',
	hair_care: 'service',
	hair_salon: 'service',
	hospital: 'service',
	insurance_agency: 'service',
	karaoke: 'service',
	laundry: 'service',
	lawyer: 'service',
	local_government_office: 'service',
	locksmith: 'service',
	makeup_artist: 'service',
	massage: 'service',
	medical_lab: 'service',
	moving_company: 'service',
	nail_salon: 'service',
	neighborhood_police_station: 'service',
	painter: 'service',
	pharmacy: 'service',
	physiotherapist: 'service',
	plumber: 'service',
	police: 'service',
	post_office: 'service',
	preschool: 'service',
	primary_schoolschool: 'service',
	psychic: 'service',
	public_bath: 'service',
	public_bathroom: 'service',
	real_estate_agency: 'service',
	roofing_contractor: 'service',
	sauna: 'service',
	school: 'service',
	secondary_school: 'service',
	skin_care_clinic: 'service',
	spa: 'service',
	sports_activity_location: 'service',
	sports_club: 'service',
	sports_coaching: 'service',
	sports_complex: 'service',
	stable: 'service',
	storage: 'service',
	summer_camp_organizer: 'service',
	swimming_pool: 'service',
	tailor: 'service',
	tanning_studio: 'service',
	telecommunications_service_provider: 'service',
	tour_agency: 'service',
	tourist_information_center: 'service',
	travel_agency: 'service',
	university: 'service',
	veterinary_care: 'service',
	wellness_center: 'service',
	wedding_venue: 'service',
	yoga_studio: 'service',
	bus_station: 'transport',
	bus_stop: 'transport',
	car_rental: 'transport',
	electric_vehicle_charging_station: 'transport',
	ferry_terminal: 'transport',
	gas_station: 'transport',
	light_rail_station: 'transport',
	park_and_ride: 'transport',
	parking: 'transport',
	subway_station: 'transport',
	taxi_stand: 'transport',
	train_station: 'transport',
	transit_depot: 'transport',
	transit_station: 'transport',
	truck_stop: 'transport',
	beach: 'beach',
	amusement_park: 'touristAttraction',
	amphitheatre: 'touristAttraction',
	aquarium: 'touristAttraction',
	archipelago: 'touristAttraction',
	banquet_hall: 'touristAttraction',
	barbecue_area: 'touristAttraction',
	bowling_alley: 'touristAttraction',
	casino: 'touristAttraction',
	cemetery: 'touristAttraction',
	childrens_camp: 'touristAttraction',
	church: 'touristAttraction',
	city_hall: 'touristAttraction',
	colloquial_area: 'touristAttraction',
	community_center: 'touristAttraction',
	continent: 'touristAttraction',
	country: 'touristAttraction',
	courthouse: 'touristAttraction',
	convention_center: 'touristAttraction',
	cycling_park: 'touristAttraction',
	establishment: 'touristAttraction',
	event_venue: 'touristAttraction',
	embassy: 'touristAttraction',
	ferris_wheel: 'touristAttraction',
	finance: 'touristAttraction',
	floor: 'touristAttraction',
	food: 'touristAttraction',
	geocode: 'touristAttraction',
	health: 'touristAttraction',
	hindu_temple: 'touristAttraction',
	historical_landmark: 'touristAttraction',
	intersection: 'touristAttraction',
	landmark: 'touristAttraction',
	monument: 'touristAttraction',
	mosque: 'touristAttraction',
	natural_feature: 'touristAttraction',
	neighborhood: 'touristAttraction',
	observation_deck: 'touristAttraction',
	off_roading_area: 'touristAttraction',
	place_of_worship: 'touristAttraction',
	plaza: 'touristAttraction',
	plus_code: 'touristAttraction',
	point_of_interest: 'touristAttraction',
	political: 'touristAttraction',
	post_box: 'touristAttraction',
	postal_code: 'touristAttraction',
	postal_code_prefix: 'touristAttraction',
	postal_code_suffix: 'touristAttraction',
	postal_town: 'touristAttraction',
	premise: 'touristAttraction',
	roller_coaster: 'touristAttraction',
	room: 'touristAttraction',
	route: 'touristAttraction',
	sculpture: 'touristAttraction',
	ski_resort: 'touristAttraction',
	stadium: 'touristAttraction',
	street_address: 'touristAttraction',
	street_number: 'touristAttraction',
	sublocality: 'touristAttraction',
	sublocality_level_1: 'touristAttraction',
	sublocality_level_2: 'touristAttraction',
	sublocality_level_3: 'touristAttraction',
	sublocality_level_4: 'touristAttraction',
	sublocality_level_5: 'touristAttraction',
	subpremise: 'touristAttraction',
	synagogue: 'touristAttraction',
	town_square: 'touristAttraction',
	tourist_attraction: 'touristAttraction',
	video_arcade: 'touristAttraction',
	visitor_center: 'touristAttraction',
	water_park: 'touristAttraction',
}
